import { useState, type ReactElement, type ReactNode } from 'react'

import { useTranslate } from '@/src/lang/useTranslate'

import type { TDefaultButtonProps } from '../../button/default'
import { Modal, type TModalProps } from '../../modal/Modal'

export type TModalInputProps = Omit<TModalProps, 'children' | 'showModal' | 'onConfirm'> & {
  /** This onSubmit should throw an error - whatever it is - if it fails, so that the modal is not closed */
  onUncaughtSubmit: () => Promise<void>
  children: ReactNode | ((inputProps: { onSubmitEditing: VoidFunction }) => ReactNode)
  renderTrigger: (triggerProp: { onTrigger: TDefaultButtonProps['onPress'] }) => ReactElement
}

export const ModalInput = ({
  children,
  onCancel,
  onUncaughtSubmit,
  renderTrigger,
  ...otherProps
}: TModalInputProps): ReactNode => {
  const [showModal, setShowModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslate()

  const onClose = () => setShowModal(false)
  const handleConfirm = () => {
    setIsLoading(true)
    onUncaughtSubmit()
      .then(onClose)
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }

  return (
    <>
      <Modal
        hideCloseButton
        showCancelButton
        confirmButtonProps={{ variant: 'faded', isLoading }}
        classNames={{ body: 'pt-2', header: 'px-4' }}
        showModal={showModal}
        confirmText={t('word.save')}
        onCancel={() => {
          onCancel?.()
          onClose()
        }}
        onConfirm={handleConfirm}
        {...otherProps}
      >
        {typeof children === 'function' ? children({ onSubmitEditing: handleConfirm }) : children}
      </Modal>
      {renderTrigger({ onTrigger: () => setShowModal(true) })}
    </>
  )
}
